import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CopyOutlined, InfoCircleFilled } from "@ant-design/icons";
import { App, AutoComplete, Form, Input, theme } from "antd";
import { observer } from "mobx-react";
import { useMemo, useRef, useState } from "react";
import { copyToClipboard } from "@devowl-wp/react-utils";
import { RealCommerceError } from "../../../../../../exceptions/realCommerceError.js";
import { useRealCommerceErrorHandling } from "../../../../../../hooks/useRealCommerceErrorHandling.js";
import { trackAnalyticsEvent } from "../../../../../../others/tracking.js";
import { useStores } from "../../../../../../store/stores.js";
import { __ } from "../../../../../../utils/i18n.js";
import { MATOMO_MASK } from "../../../../../../utils/matomoMask.js";
import { InfoCard } from "../../../infoCard.js";
import { AffiliatePanel } from "../affiliatePanel.js";
// Hardcoded landing-page list extracted from the Paddle affiliate vendor list;
// de-hardcoding is intentionally out of scope.
const PREDEFINED_LINK_LIST = {
    DE: {
        "devowl.io Startseite": "/de/",
        "Real Cookie Banner": "/de/wordpress-real-cookie-banner/",
        "Real Cookie Banner vs. alternative WordPress Cookie Plugins": "/de/wordpress-real-cookie-banner/vergleichen/",
        "Real Cookie Banner: Entwickler API": "/de/wordpress-real-cookie-banner/entwickler-api/",
        "Real Category Management": "/de/wordpress-real-category-management/",
        "Real Media Library": "/de/wordpress-real-media-library/",
        "Real Physical Media": "/de/wordpress-real-physical-media/",
        "Real Thumbnail Generator": "/de/wordpress-real-thumbnail-generator/"
    },
    EN: {
        "devowl.io frontpage": "/",
        "Real Cookie Banner": "/wordpress-real-cookie-banner/",
        "Real Cookie Banner vs. alternative WordPress Cookie Plugins": "/wordpress-real-cookie-banner/compare/",
        "Real Cookie Banner: Developer API": "/wordpress-real-cookie-banner/developer-api/",
        "Real Category Management": "/wordpress-real-category-management/",
        "Real Media Library": "/wordpress-real-media-library/",
        "Real Physical Media": "/wordpress-real-physical-media/",
        "Real Thumbnail Generator": "/wordpress-real-thumbnail-generator/"
    }
};
const PREDEFINED_LINK_DOMAIN = "https://devowl.io";
const LinkGenerator = observer(()=>{
    const { token } = theme.useToken();
    const { message } = App.useApp();
    const [targetLinkTerm, setTargetLinkTerm] = useState("");
    const [link, setLink] = useState(undefined);
    const debounceRef = useRef();
    const handleRealCommerceError = useRealCommerceErrorHandling();
    const { accountV2Store: { session } } = useStores();
    const affiliate = session?.affiliate;
    const generate = (target)=>{
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async ()=>{
            try {
                const url = new URL(target);
                if (!url.protocol.startsWith("http")) {
                    return;
                }
            } catch  {
                return;
            }
            try {
                const result = await affiliate?.createLink(target);
                setLink(result);
                if (result?.affiliateLink) {
                    trackAnalyticsEvent("affiliate_link_generated", {
                        ga: {
                            event_category: "account",
                            event_label: "Affiliate link generated"
                        },
                        matomo: {
                            category: "account",
                            name: "Affiliate link generated"
                        }
                    });
                    // Copy the freshly generated link straight to the clipboard.
                    copyToClipboard(result.affiliateLink);
                    message.success(__("Affiliate link generated and copied to clipboard."));
                }
            } catch (e) {
                if (e instanceof RealCommerceError) {
                    handleRealCommerceError(e);
                }
            }
        }, 400);
    };
    const autocompleteOptions = useMemo(()=>Object.keys(PREDEFINED_LINK_LIST).reduce((result, lang)=>{
            result.push({
                label: lang === "DE" ? __("German") : lang === "EN" ? __("English") : lang,
                options: Object.keys(PREDEFINED_LINK_LIST[lang]).map((label)=>({
                        key: `${lang}${label}`,
                        value: `${PREDEFINED_LINK_DOMAIN}${PREDEFINED_LINK_LIST[lang][label]}`,
                        label
                    })).filter(({ label, value })=>targetLinkTerm ? label.toLowerCase().indexOf(targetLinkTerm.toLowerCase()) > -1 || value.toLowerCase().indexOf(targetLinkTerm.toLowerCase()) > -1 : true)
            });
            return result;
        }, []).filter(({ options: { length } })=>length), [
        targetLinkTerm
    ]);
    const copy = async ()=>{
        if (link) {
            copyToClipboard(link.affiliateLink);
            message.success(__("Copied to clipboard."));
        }
    };
    return /*#__PURE__*/ _jsxs(AffiliatePanel, {
        heading: __("Generate affiliate link"),
        subline: __("Pick a target page or paste any URL on devowl.io. We'll add your unique attribution."),
        children: [
            /*#__PURE__*/ _jsxs(Form, {
                layout: "vertical",
                children: [
                    /*#__PURE__*/ _jsx(Form.Item, {
                        label: __("Target link"),
                        extra: __("Choose from suggestions or type a custom URL."),
                        children: /*#__PURE__*/ _jsx(AutoComplete, {
                            options: autocompleteOptions,
                            onSearch: setTargetLinkTerm,
                            onChange: (value)=>generate(value),
                            placeholder: __("e.g. https://devowl.io/wordpress-real-cookie-banner/")
                        })
                    }),
                    /*#__PURE__*/ _jsx(Form.Item, {
                        label: __("Affiliate link"),
                        children: /*#__PURE__*/ _jsx(Input, {
                            readOnly: true,
                            value: link?.affiliateLink ?? "",
                            style: {
                                fontFamily: "monospace"
                            },
                            ...MATOMO_MASK,
                            addonAfter: /*#__PURE__*/ _jsxs("button", {
                                type: "button",
                                className: "rc-account-v2-affiliate-copy",
                                onClick: copy,
                                disabled: !link,
                                "aria-label": __("Copy affiliate link"),
                                children: [
                                    /*#__PURE__*/ _jsx(CopyOutlined, {}),
                                    " ",
                                    __("Copy")
                                ]
                            })
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(InfoCard, {
                variant: "soft-trust",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "rc-account-v2-affiliate-note",
                    children: [
                        /*#__PURE__*/ _jsx(InfoCircleFilled, {
                            style: {
                                color: token.colorPrimary
                            }
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            children: __("We don't support adding query parameters to your link. Our attribution works without cookies and survives ad blockers - appending parameters would break it.")
                        })
                    ]
                })
            })
        ]
    });
});
export { LinkGenerator };
